import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
  Chip,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link } from 'react-router-dom';

function getOptionalAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (!sessionToken) return {};
  return { Authorization: `Bearer ${sessionToken}` };
}

/**
 * Instagram share URLs include a junk `?igsh=...` (and sometimes `#`) tail that
 * tracks who shared the link. We don't want that stored in DB — strip query +
 * fragment + trailing slash for any instagram.com URL. Non-Instagram URLs pass
 * through untouched. Runs both on input change (so the UI shows the cleaned
 * value as the admin pastes) and at submit time (defensive double-clean).
 */
function cleanCreditCtaLink(raw) {
  if (raw == null) return raw;
  const s = String(raw);
  if (!/(^|\/\/|\.)instagram\.com\//i.test(s)) return s;
  // Drop query + fragment, then trim trailing slashes.
  return s.split('?')[0].split('#')[0].replace(/\/+$/, '');
}

function CreateFeed() {
  const isCreatedByAdmin = useSelector((state) => state.roleReducer.isCreatedByAdmin);
  const loggedInUserId = useSelector((state) => state.roleReducer.userId);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Feed topics
  const [feedTopics, setFeedTopics] = useState([]);
  const [feedTopicsLoading, setFeedTopicsLoading] = useState(false);
  const [selectedFeedTopic, setSelectedFeedTopic] = useState(null);
  
  // Competitions
  const [competitions, setCompetitions] = useState([]);
  const [competitionsLoading, setCompetitionsLoading] = useState(true);
  const [selectedCompetition, setSelectedCompetition] = useState(null);

  // Matches and players
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  
  // Tags/hashtags
  const [allTags, setAllTags] = useState([]);
  const [searchedTags, setSearchedTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearchKeyword, setTagSearchKeyword] = useState('');
  
  // Content and media
  const [content, setContent] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [mediaType, setMediaType] = useState(null); // 'image' or 'video'
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  // Credits (for video posts)
  const [creditText, setCreditText] = useState('');
  const [creditCtaLink, setCreditCtaLink] = useState('');
  const [creditIconUrl, setCreditIconUrl] = useState('');

  // Form state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Load users - parent users see only their children, admins see all
  useEffect(() => {
    const headers = { ...getOptionalAuthHeaders() };
    const url = isCreatedByAdmin && loggedInUserId
      ? `${BASE_URL}/admin/users/children/${loggedInUserId}?limit=500&offset=0`
      : `${BASE_URL}/admin/users/created-by-admin?limit=500&offset=0`;
    fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const users = data?.data?.users ?? data?.users ?? [];
        setAdminUsers(Array.isArray(users) ? users : []);
      })
      .catch(() => setAdminUsers([]))
      .finally(() => setAdminUsersLoading(false));
  }, [isCreatedByAdmin, loggedInUserId]);

  // Load competitions
  useEffect(() => {
    setCompetitionsLoading(true);
    fetch(`${BASE_URL}/competitions?limit=50&offset=0`, {
      method: 'GET',
      credentials: 'include',
      headers: { ...getOptionalAuthHeaders() },
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const list = data?.data?.competitions ?? data?.competitions ?? [];
        setCompetitions(Array.isArray(list) ? list : []);
      })
      .catch(() => setCompetitions([]))
      .finally(() => setCompetitionsLoading(false));
  }, []);

  // Load feed topics
  useEffect(() => {
    const sessionToken = sessionStorage.getItem('sessionToken');
    const headers = {};
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    setFeedTopicsLoading(true);
    fetch(`${BASE_URL}/admin/feed-topics?limit=50&offset=0`, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const topics = data?.data?.feedTopics ?? data?.feedTopics ?? [];
        setFeedTopics(Array.isArray(topics) ? topics : []);
      })
      .catch(() => setFeedTopics([]))
      .finally(() => setFeedTopicsLoading(false));
  }, []);

  // Load feed topics by match ID when match is selected
  useEffect(() => {
    const mid = selectedMatch?.match_id ?? selectedMatch?.id ?? selectedMatch?.mid;
    if (!mid || selectedFeedTopic) return; // Don't override if topic already selected
    
    const sessionToken = sessionStorage.getItem('sessionToken');
    const headers = {};
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    fetch(`${BASE_URL}/admin/feed-topics/mid/${mid}?limit=20&offset=0`, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const topics = data?.data?.feedTopics ?? data?.feedTopics ?? [];
        if (Array.isArray(topics) && topics.length > 0) {
          setFeedTopics(topics);
        }
      })
      .catch(() => {});
  }, [selectedMatch, selectedFeedTopic]);

  // Load matches
  useEffect(() => {
    const sessionToken = sessionStorage.getItem('sessionToken');
    const headers = {};
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    setMatchesLoading(true);
    fetch(`${BASE_URL}/competitions/live/matches/new`, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        // Backend returns snake_case (`ongoing_matches`); older camelCase
        // (`ongoingMatches`) and plain `matches` kept as fallbacks so any
        // future shape change doesn't break the dropdown again.
        const list =
          data?.data?.ongoing_matches ??
          data?.data?.ongoingMatches ??
          data?.data?.matches ??
          data?.ongoing_matches ??
          data?.matches ??
          [];
        setMatches(Array.isArray(list) ? list : []);
      })
      .catch(() => setMatches([]))
      .finally(() => setMatchesLoading(false));
  }, []);

  // Load players when match is selected
  useEffect(() => {
    const mid = selectedMatch?.match_id ?? selectedMatch?.id ?? selectedMatch?.mid;
    if (!mid) {
      setPlayers([]);
      setSelectedPlayer(null);
      return;
    }
    const sessionToken = sessionStorage.getItem('sessionToken');
    const headers = {};
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    setPlayersLoading(true);
    setSelectedPlayer(null);
    fetch(`${BASE_URL}/players/live/playing-eleven?mid=${mid}&limit=500&offset=0`, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const list = data?.data?.playingEleven ?? data?.data?.players ?? data?.players ?? [];
        setPlayers(Array.isArray(list) ? list : []);
      })
      .catch(() => setPlayers([]))
      .finally(() => setPlayersLoading(false));
  }, [selectedMatch]);

  // Load tags/hashtags (default list)
  useEffect(() => {
    const sessionToken = sessionStorage.getItem('sessionToken');
    const headers = {};
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    setTagsLoading(true);
    fetch(`${BASE_URL}/admin/tags?limit=100&offset=0`, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        const tagList = data?.data?.tags ?? data?.tags ?? [];
        const normalized = Array.isArray(tagList) ? tagList : [];
        normalized.sort((a, b) => (b?.trending_score ?? 0) - (a?.trending_score ?? 0));
        setAllTags(normalized);
      })
      .catch(() => setAllTags([]))
      .finally(() => setTagsLoading(false));
  }, []);

  // Search tags/hashtags (optional)
  useEffect(() => {
    if (!tagSearchKeyword || tagSearchKeyword.trim().length < 2) {
      setSearchedTags([]);
      return;
    }
    const sessionToken = sessionStorage.getItem('sessionToken');
    const headers = {};
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    setTagsLoading(true);
    const searchTimeout = setTimeout(() => {
      fetch(
        `${BASE_URL}/admin/tags?keyword=${encodeURIComponent(tagSearchKeyword.trim())}&limit=50&offset=0`,
        {
          method: 'GET',
          credentials: 'include',
          headers,
        },
      )
        .then((res) => res.json().catch(() => ({})))
        .then((data) => {
          const tagList = data?.data?.tags ?? data?.tags ?? [];
          const normalized = Array.isArray(tagList) ? tagList : [];
          normalized.sort((a, b) => (b?.trending_score ?? 0) - (a?.trending_score ?? 0));
          setSearchedTags(normalized);
        })
        .catch(() => setSearchedTags([]))
        .finally(() => setTagsLoading(false));
    }, 300);
    return () => clearTimeout(searchTimeout);
  }, [tagSearchKeyword]);

  const tagsOptions = tagSearchKeyword.trim().length >= 2 ? searchedTags : allTags;

  const previewUserName =
    selectedUser?.username ??
    selectedUser?.user_name ??
    selectedUser?.name ??
    'username';

  const previewUserInitial = previewUserName.charAt(0).toUpperCase();

  const previewCompetitionChipLabel = selectedCompetition
    ? selectedCompetition.name ?? selectedCompetition.title ?? `CID ${selectedCompetition.cid ?? selectedCompetition.id ?? ''}`
    : null;

  const previewMatchChipLabel = selectedMatch
    ? selectedMatch.title ??
      selectedMatch.name ??
      selectedMatch.subtitle ??
      `Match ${selectedMatch.match_id ?? selectedMatch.id ?? ''}`
    : null;

  const previewPlayerChipLabel = selectedPlayer
    ? selectedPlayer.name ??
      selectedPlayer.player_name ??
      selectedPlayer.title ??
      `Player ${selectedPlayer.player_id ?? selectedPlayer.id ?? ''}`
    : null;

  const handleMediaUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploadLoading(true);
    const headers = { 'Content-Type': 'application/json', ...getOptionalAuthHeaders() };
    try {
      const isVideo = file.type?.startsWith('video/');
      const detectedMediaType = isVideo ? 'video/mp4' : 'image';
      setMediaType(detectedMediaType);

      const presignedUrlStr = `${BASE_URL}/admin/posts/presigned-url`;
      const presignedRes = await fetch(presignedUrlStr, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          mediaName: file.name,
          mediaType: isVideo ? 'video' : 'image',
        }),
      });
      if (presignedRes.status === 401 || presignedRes.status === 403) {
        setUploadError('Unauthorized. Please log in again.');
        return;
      }
      const presignedData = await presignedRes.json().catch(() => ({}));
      
      const uploadUrl = presignedData?.data?.presignedUrl ?? presignedData?.presignedUrl;
      const finalUrl = presignedData?.data?.image_url ?? presignedData?.data?.url ?? presignedData?.image_url;
      
      if (!uploadUrl) {
        setUploadError(presignedData?.message || presignedData?.error || 'Could not get upload URL');
        return;
      }
      
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || (detectedMediaType === 'video' ? 'video/mp4' : 'image/jpeg') },
      });
      
      if (!putRes.ok) {
        setUploadError('Upload to S3 failed');
        return;
      }
      
      const urlToUse = finalUrl || uploadUrl.split('?')[0];
      setUploadedImageUrl(urlToUse);
    } catch (err) {
      setUploadError(err?.message || 'Upload failed');
    } finally {
      setUploadLoading(false);
      e.target.value = '';
    }
  };

  const handleRemoveMedia = () => {
    setUploadedImageUrl('');
    setMediaType(null);
    setUploadError('');
    setCreditText('');
    setCreditCtaLink('');
    setCreditIconUrl('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const userId = selectedUser?.id ?? selectedUser?.user_id;
    const feedTopicId = selectedFeedTopic?.id ?? selectedFeedTopic?.feed_topic_id;
    const matchIdVal = selectedMatch?.match_id ?? selectedMatch?.id ?? selectedMatch?.mid;
    const playerIdVal = selectedPlayer?.player_id ?? selectedPlayer?.id ?? selectedPlayer?.pid;
    const cidVal = selectedCompetition?.cid ?? selectedCompetition?.id;
    const contentTrimmed = content.trim();

    // Validation
    if (!userId) {
      setError('User is required');
      return;
    }
    if (!contentTrimmed) {
      setError('Content is required');
      return;
    }

    // If no feed topic and no competition selected, match_id and player_id are required
    if (!feedTopicId && !cidVal) {
      if (!matchIdVal) {
        setError('Match is required when feed topic and competition are not selected');
        return;
      }
      if (!playerIdVal) {
        setError('Player is required when feed topic and competition are not selected');
        return;
      }
    }

    setLoading(true);
    
    const payload = {
      content: contentTrimmed,
      user_id: userId,
    };
    
    if (feedTopicId) {
      payload.feed_topic_id = feedTopicId;
    }
    
    if (matchIdVal) {
      payload.match_id = matchIdVal;
    }
    
    if (playerIdVal) {
      payload.player_id = playerIdVal;
    }

    if (cidVal) {
      payload.cid = cidVal;
    }
    
    if (selectedTags.length > 0) {
      payload.tags = selectedTags.map(tag => ({
        id: tag.id,
        tag: tag.tag,
      }));
    }
    
    if (uploadedImageUrl && mediaType) {
      payload.image_url = uploadedImageUrl;
      payload.media_type = mediaType;
      payload.media_uploaded = true;
    }

    if (creditText.trim()) {
      payload.credit_text = creditText.trim();
    }
    if (creditCtaLink.trim()) {
      payload.credit_cta_link = cleanCreditCtaLink(creditCtaLink.trim());
    }
    if (creditIconUrl.trim()) {
      payload.credit_icon_url = creditIconUrl.trim();
    }
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getOptionalAuthHeaders(),
      };
      const createFeedUrlStr = `${BASE_URL}/admin/posts`;
      const response = await fetch(createFeedUrlStr, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(payload),
      });

      if (response.status === 401 || response.status === 403) {
        setError('Unauthorized. Please log in again.');
        return;
      }
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data?.message || data?.error || 'Failed to create feed');
        return;
      }
      setSuccess('Feed created successfully.');
      // Reset form
      setSelectedUser(null);
      setSelectedFeedTopic(null);
      setSelectedCompetition(null);
      setSelectedMatch(null);
      setSelectedPlayer(null);
      setPlayers([]);
      setSelectedTags([]);
      setContent('');
      setUploadedImageUrl('');
      setMediaType(null);
      setCreditText('');
      setCreditCtaLink('');
      setCreditIconUrl('');
    } catch (err) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center ">
                  <IconButton
                    style={{ background: '#FFFFFF' }}
                    component={Link}
                    to="/"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <h3 style={{ marginLeft: '20px' }}>Create Feed</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Paper elevation={0} sx={{ p: 2, maxWidth: 800 }}>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                      </Alert>
                    )}
                    {success && (
                      <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                      </Alert>
                    )}
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            options={adminUsers}
                            loading={adminUsersLoading}
                            getOptionLabel={(option) =>
                              `${option.id ?? option.user_id ?? '—'} - ${option.username ?? option.user_name ?? ''}${option.name ? ` (${option.name})` : ''}`
                            }
                            value={selectedUser}
                            onChange={(_, newValue) => setSelectedUser(newValue)}
                            isOptionEqualToValue={(option, value) =>
                              (option.id ?? option.user_id) === (value?.id ?? value?.user_id)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="User"
                                required
                                variant="outlined"
                                helperText="Required. Select an admin-created user."
                              />
                            )}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            options={feedTopics}
                            loading={feedTopicsLoading}
                            getOptionLabel={(option) =>
                              option.topic_name ?? option.name ?? `Topic #${option.id ?? '—'}`
                            }
                            value={selectedFeedTopic}
                            onChange={(_, newValue) => {
                              setSelectedFeedTopic(newValue);
                              // If topic selected, match/player become optional
                            }}
                            isOptionEqualToValue={(option, value) =>
                              (option.id ?? option.feed_topic_id) === (value?.id ?? value?.feed_topic_id)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Feed Topic"
                                variant="outlined"
                                helperText="Optional. If selected, match and player become optional."
                              />
                            )}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            options={competitions}
                            loading={competitionsLoading}
                            getOptionLabel={(option) =>
                              `${option.cid ?? option.id ?? '—'} - ${option.name ?? option.title ?? ''}`
                            }
                            value={selectedCompetition}
                            onChange={(_, newValue) => setSelectedCompetition(newValue)}
                            isOptionEqualToValue={(option, value) =>
                              (option.cid ?? option.id) === (value?.cid ?? value?.id)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Competition"
                                variant="outlined"
                                helperText="Optional. If selected, match and player become optional."
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            options={matches}
                            loading={matchesLoading}
                            getOptionLabel={(option) =>
                              [option.match_id ?? option.id ?? option.mid, option.title ?? option.name ?? option.subtitle ?? ''].filter(Boolean).join(' - ') || '—'
                            }
                            value={selectedMatch}
                            onChange={(_, newValue) => setSelectedMatch(newValue)}
                            isOptionEqualToValue={(option, value) =>
                              (option.match_id ?? option.id ?? option.mid) === (value?.match_id ?? value?.id ?? value?.mid)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Match"
                                variant="outlined"
                                required={!selectedFeedTopic && !selectedCompetition}
                                helperText={selectedFeedTopic || selectedCompetition ? "Optional." : "Required when feed topic and competition are not selected."}
                              />
                            )}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            options={players}
                            loading={playersLoading}
                            disabled={!selectedMatch}
                            getOptionLabel={(option) =>
                              [option.player_id ?? option.id ?? option.pid, option.name ?? option.title ?? option.player_name ?? ''].filter(Boolean).join(' - ') || '—'
                            }
                            value={selectedPlayer}
                            onChange={(_, newValue) => setSelectedPlayer(newValue)}
                            isOptionEqualToValue={(option, value) =>
                              (option.player_id ?? option.id ?? option.pid) === (value?.player_id ?? value?.id ?? value?.pid)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Player"
                                variant="outlined"
                                required={!selectedFeedTopic && !selectedCompetition}
                                helperText={selectedFeedTopic || selectedCompetition ? "Optional." : "Required when feed topic and competition are not selected. Select a match first."}
                              />
                            )}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Content"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            required
                            multiline
                            minRows={3}
                            variant="outlined"
                            helperText="Required. Post text content."
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            multiple
                            options={tagsOptions}
                            loading={tagsLoading}
                            value={selectedTags}
                            onChange={(_, newValue) => setSelectedTags(newValue)}
                            inputValue={tagSearchKeyword}
                            onInputChange={(_, newInputValue) => setTagSearchKeyword(newInputValue)}
                            getOptionLabel={(option) => option.tag ?? option.name ?? ''}
                            isOptionEqualToValue={(option, value) =>
                              (option.id ?? option.tag_id) === (value?.id ?? value?.tag_id)
                            }
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  label={option.tag ?? option.name}
                                  {...getTagProps({ index })}
                                  key={option.id ?? option.tag_id ?? index}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Tags / Hashtags"
                                variant="outlined"
                                helperText="Optional. Select from list, or type 2+ characters to search."
                                placeholder="Type to search..."
                              />
                            )}
                            noOptionsText={tagSearchKeyword.trim().length < 2 ? "No more tags" : "No tags found"}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Box>
                            <input
                              accept="image/*,video/*"
                              type="file"
                              id="feed-media-upload"
                              hidden
                              onChange={handleMediaUpload}
                              disabled={uploadLoading}
                            />
                            <label htmlFor="feed-media-upload">
                              <Button
                                component="span"
                                variant="outlined"
                                startIcon={uploadLoading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                                disabled={uploadLoading}
                              >
                                {uploadLoading ? 'Uploading…' : 'Upload Image/Video'}
                              </Button>
                            </label>
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                              Optional. Image or video uploaded to S3 via presigned URL.
                            </Typography>
                            {uploadError && (
                              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploadError('')}>
                                {uploadError}
                              </Alert>
                            )}
                            {uploadedImageUrl && (
                              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {mediaType?.startsWith('video') ? (
                                  <video
                                    src={uploadedImageUrl}
                                    controls
                                    style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8 }}
                                  />
                                ) : (
                                  <img
                                    src={uploadedImageUrl}
                                    alt="Feed"
                                    style={{ maxWidth: 120, maxHeight: 120, objectFit: 'contain', borderRadius: 8 }}
                                  />
                                )}
                                <Button
                                  size="small"
                                  color="secondary"
                                  startIcon={<DeleteOutlineIcon />}
                                  onClick={handleRemoveMedia}
                                >
                                  Remove
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </Grid>

                        {uploadedImageUrl && (
                          <>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Credit Text"
                                value={creditText}
                                onChange={(e) => setCreditText(e.target.value)}
                                variant="outlined"
                                helperText="Optional. Credit text shown on the post."
                                placeholder="e.g. Photo by @username"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Credit CTA Link"
                                value={creditCtaLink}
                                onChange={(e) => setCreditCtaLink(cleanCreditCtaLink(e.target.value))}
                                onPaste={(e) => {
                                  // Clean on paste too — handles users pasting an Instagram share URL with the ?igsh= tail.
                                  const pasted = (e.clipboardData || window.clipboardData)?.getData('text');
                                  if (pasted && /instagram\.com\//i.test(pasted)) {
                                    e.preventDefault();
                                    setCreditCtaLink(cleanCreditCtaLink(pasted));
                                  }
                                }}
                                variant="outlined"
                                helperText="Optional. Link that opens when credit is tapped. Instagram share URLs are auto-trimmed (e.g. ?igsh=... is stripped)."
                                placeholder="https://..."
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Credit Icon URL"
                                value={creditIconUrl}
                                onChange={(e) => setCreditIconUrl(e.target.value)}
                                variant="outlined"
                                helperText="Optional. Icon/logo image URL shown with the credit."
                                placeholder="https://...icon.png"
                              />
                            </Grid>
                          </>
                        )}

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
                            Preview
                          </Typography>
                          <Box
                            sx={{
                              maxWidth: 360,
                              bgcolor: '#1b1033',
                              color: '#ffffff',
                              borderRadius: 2,
                              p: 2,
                              boxShadow: 3,
                            }}
                          >
                            {(previewCompetitionChipLabel || previewMatchChipLabel || previewPlayerChipLabel) && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75,
                                  mb: 1.5,
                                }}
                              >
                                {previewCompetitionChipLabel && (
                                  <Chip
                                    size="small"
                                    label={previewCompetitionChipLabel}
                                    sx={{
                                      bgcolor: 'rgba(0,0,0,0.3)',
                                      color: '#ffffff',
                                      fontSize: 11,
                                    }}
                                  />
                                )}
                                {previewMatchChipLabel && (
                                  <Chip
                                    size="small"
                                    label={previewMatchChipLabel}
                                    sx={{
                                      bgcolor: 'rgba(0,0,0,0.3)',
                                      color: '#ffffff',
                                      fontSize: 11,
                                    }}
                                  />
                                )}
                                {previewPlayerChipLabel && (
                                  <Chip
                                    size="small"
                                    label={previewPlayerChipLabel}
                                    sx={{
                                      bgcolor: 'rgba(0,0,0,0.3)',
                                      color: '#ffffff',
                                      fontSize: 11,
                                    }}
                                  />
                                )}
                              </Box>
                            )}

                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1.5,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: '#7c4dff',
                                    fontSize: 14,
                                  }}
                                >
                                  {previewUserInitial}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, lineHeight: 1.1 }}
                                  >
                                    {previewUserName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                                  >
                                    0 min
                                  </Typography>
                                </Box>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{
                                  textTransform: 'none',
                                  borderColor: 'rgba(255,255,255,0.5)',
                                  color: '#ffffff',
                                  fontSize: 11,
                                  paddingInline: 1.5,
                                  paddingBlock: 0.25,
                                }}
                              >
                                Follow
                              </Button>
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{ mb: selectedTags.length > 0 ? 1 : 1.5, whiteSpace: 'pre-line' }}
                            >
                              {content.trim() || 'Post content will appear here…'}
                            </Typography>

                            {selectedTags.length > 0 && (
                              <Box
                                sx={{
                                  mb: uploadedImageUrl ? 1.5 : 1,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75,
                                }}
                              >
                                {selectedTags.map((tag) => {
                                  const label = tag.tag ?? tag.name ?? '';
                                  const withHash = label.startsWith('#') ? label : `#${label}`;
                                  return (
                                    <Typography
                                      key={tag.id ?? tag.tag_id ?? withHash}
                                      variant="body2"
                                      sx={{ color: '#7c4dff', fontWeight: 500 }}
                                    >
                                      {withHash}
                                    </Typography>
                                  );
                                })}
                              </Box>
                            )}

                            {uploadedImageUrl && (
                              <Box
                                sx={{
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  bgcolor: '#000000',
                                  mb: 1.5,
                                  position: 'relative',
                                }}
                              >
                                {mediaType?.startsWith('video') ? (
                                  <video
                                    src={uploadedImageUrl}
                                    style={{ width: '100%', display: 'block' }}
                                    controls
                                  />
                                ) : (
                                  <img
                                    src={uploadedImageUrl}
                                    alt="Preview"
                                    style={{ width: '100%', display: 'block' }}
                                  />
                                )}
                                {creditText.trim() && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      bgcolor: 'rgba(0,0,0,0.6)',
                                      borderRadius: 1,
                                      px: 1,
                                      py: 0.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    {creditIconUrl.trim() && (
                                      <img
                                        src={creditIconUrl}
                                        alt=""
                                        style={{ width: 14, height: 14, borderRadius: 2, objectFit: 'cover' }}
                                      />
                                    )}
                                    <Typography variant="caption" sx={{ color: '#fff', fontSize: 10 }}>
                                      {creditText}
                                    </Typography>
                                    {creditCtaLink.trim() && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: '#7c4dff', fontWeight: 600, fontSize: 10, cursor: 'pointer' }}
                                      >
                                        Open
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            )}

                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-around',
                                mt: 0.5,
                                pt: 1,
                                borderTop: '1px solid rgba(255,255,255,0.08)',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Like
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Comment
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Blast
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                          >
                            {loading ? 'Creating...' : 'Create Feed'}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Paper>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default CreateFeed;
